import * as React from 'react';
import styles from './VideoAnimation.scss';
import classNames from 'classnames';
import { getSupportedVideo } from '~/utils/video';
import { isMobileOrTabletWindow } from '~/utils/utils';
import { findParentNode } from '~/utils/dom';
import useResourceLoader from '~/hooks/useResourceLoader';
import { isSafariBrowser } from '~/utils/settings';

interface IVideoAnimation {
    hoverVideo?: IVideo;
    idleVideo?: IVideo;
    className?: string;
    children: React.ReactNode | React.ReactNode[];
    isDisableAnimation?: boolean;
    delay?: number;
    muted?: boolean;
    parentClassName: string;
    [key: string]: any;
}

const VideoAnimation = (props: IVideoAnimation) => {
    const { hoverVideo, idleVideo, isDisableAnimation, children, className, muted } = props;
    const [isVisibleHoverVideo, toggleVisibilityHoverVideo] = React.useState(false);
    const hoverVideoRef = React.useRef<HTMLVideoElement>(null);
    const idleVideoRef = React.useRef<HTMLVideoElement>(null);
    const hoverVideoUrl = getSupportedVideo(hoverVideo);
    const idleVideoUrl = getSupportedVideo(idleVideo);

    const videoUrls = React.useMemo(() => {
        const urls = [];
        idleVideoUrl && urls.push(idleVideoUrl);
        hoverVideoUrl && urls.push(hoverVideoUrl);
        return urls;
    }, []);

    const [isFinishedLoading, resources] = useResourceLoader(videoUrls, isSafariBrowser, !videoUrls.length);

    const isEnabled = isFinishedLoading && !isDisableAnimation && !isMobileOrTabletWindow;
    const isEnabledHoverVideo = isEnabled && !!hoverVideoUrl;
    const isEnabledIdleVideo = isEnabled && !!idleVideoUrl;
    let timer: NodeJS.Timeout;

    const mouseEnter = () => {
        if (isEnabledHoverVideo) {
            if (hoverVideoRef.current) {
                const play = () => {
                    hoverVideoRef.current.currentTime = 0;
                    toggleVisibilityHoverVideo(true);
                    hoverVideoRef.current.play();
                    if (idleVideoRef.current) {
                        idleVideoRef.current.pause();
                    }
                };
                if (!props.delay) {
                    play();
                } else {
                    timer = setTimeout(() => {
                        play();
                        clearTimeout(timer);
                    }, props.delay);
                }
            }
        }
    };

    const mouseLeave = () => {
        clearTimeout(timer);
        if (isEnabledHoverVideo) {
            if (hoverVideoRef.current) {
                toggleVisibilityHoverVideo(false);
                hoverVideoRef.current.pause();
                if (idleVideoRef.current) {
                    idleVideoRef.current.currentTime = 0;
                    idleVideoRef.current.play();
                }
            }
        }
    };

    const classesIdleVideoWrapper = classNames(styles.videoWrapper, {
        [styles.visible]: !isVisibleHoverVideo,
    });

    const classesHoverVideoWrapper = classNames(styles.videoWrapper, {
        [styles.visible]: isVisibleHoverVideo,
    });

    React.useEffect(() => {
        if (!isEnabledHoverVideo || !hoverVideoRef.current) {
            return;
        }

        const element = findParentNode(props.parentClassName, hoverVideoRef.current);
        if (element) {
            element.addEventListener('mouseenter', mouseEnter);
            element.addEventListener('mouseleave', mouseLeave);
        }

        return () => {
            if (element) {
                element.removeEventListener('mouseenter', mouseEnter);
                element.removeEventListener('mouseleave', mouseLeave);
            }
        };
    }, [isEnabledHoverVideo]);

    return (
        <div className={classNames(styles.wrapper, className)} {...props}>
            {children}
            {isEnabledIdleVideo && (
                <div className={classesIdleVideoWrapper}>
                    <video
                        src={resources[idleVideoUrl]}
                        autoPlay
                        loop={true}
                        muted={true}
                        controls={false}
                        ref={(_ref) => {
                            if (_ref) {
                                idleVideoRef.current = _ref;
                            }
                        }}
                        preload={'metadata'}
                    />
                </div>
            )}
            {isEnabledHoverVideo && (
                <div className={classesHoverVideoWrapper}>
                    <video
                        src={resources[hoverVideoUrl]}
                        loop={true}
                        muted={muted === undefined ? true : muted}
                        controls={false}
                        ref={(_ref) => {
                            if (_ref) {
                                hoverVideoRef.current = _ref;
                            }
                        }}
                        preload={'metadata'}
                    />
                </div>
            )}
        </div>
    );
};

export default VideoAnimation;
